import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getCategoryBySlug, getArticlesByCategory, categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortMode, setSortMode] = useState<'latest' | 'hot'>('latest');
  const category = getCategoryBySlug(slug || '');
  const rawArticles = getArticlesByCategory(slug || '');

  const sortedArticles = useMemo(() => {
    if (sortMode === 'hot') {
      return [...rawArticles].sort((a, b) => b.views - a.views);
    }
    return [...rawArticles].sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
  }, [rawArticles, sortMode]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500 text-lg">分类不存在</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const featuredArticle = sortedArticles[0];
  const remainingArticles = sortedArticles.slice(1);

  return (
    <>
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={category.image}
              alt={category.name}
              title={category.name}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>

          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="mb-6">
              <nav className="inline-flex items-center gap-2 text-background-50/80 text-xs md:text-sm" aria-label="面包屑导航">
                <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
                <span className="text-background-50/50">/</span>
                <span className="text-background-50">{category.name}</span>
              </nav>
            </div>

            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-background-50 mb-4">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-background-50/70 max-w-2xl mx-auto mb-8">
              已收录 <strong className="text-accent-400 font-semibold">{category.count.toLocaleString()}</strong> 条爆料 — {category.description}
            </p>
          </div>
        </section>

        {/* Category Tabs Switcher */}
        <section className="w-full bg-background-50 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 no-scrollbar">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-full transition-colors flex-shrink-0 cursor-pointer whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-foreground-900 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  <span>{cat.name}</span>
                  <span className={`text-xs ${slug === cat.slug ? 'text-background-50/60' : 'text-foreground-400'}`}>
                    {cat.count.toLocaleString()}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Sort Tabs */}
        <section className="w-full bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-900">
                {category.name}<span className="text-accent-500"> · {sortMode === 'latest' ? '最新' : '最热'}</span>
              </h2>
              <div className="flex items-center gap-1 px-1 py-1 bg-background-100 rounded-full">
                <button
                  onClick={() => setSortMode('latest')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    sortMode === 'latest'
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  最新发布
                </button>
                <button
                  onClick={() => setSortMode('hot')}
                  className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    sortMode === 'hot'
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  本周最热
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://sxyyzn.cn" },
              { "@type": "ListItem", "position": 2, "name": category.name, "item": `https://sxyyzn.cn/category/${category.slug}` }
            ]
          })
        }} />

        {/* Article Grid */}
        <section className="w-full bg-background-50 pb-0 pt-0">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {sortedArticles.slice(0, 8).map((article, idx) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer hover:bg-background-200/70 transition-colors"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    {idx === 0 && sortMode === 'latest' && (
                      <div className="absolute top-3 left-3">
                        <span className="inline-block px-2.5 py-0.5 text-xs font-medium bg-foreground-900 text-background-50 rounded-full whitespace-nowrap">
                          置顶
                        </span>
                      </div>
                    )}
                    {sortMode === 'hot' && idx < 3 && (
                      <div className="absolute top-3 left-3">
                        <span className="inline-block px-2.5 py-0.5 text-xs font-medium bg-accent-500 text-background-50 rounded-full whitespace-nowrap">
                          TOP{idx + 1}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-bold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <span>{article.publishDate}</span>
                      <span><i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Feature Grid - 深度调查 */}
        <section className="w-full bg-background-100 py-16 md:py-20 mt-10">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                深度<span className="text-accent-500"> 调查</span>
              </h2>
              <p className="text-sm text-foreground-500">编辑精选的深度报道与专题追踪</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {featuredArticle && (
                <Link
                  to={`/article/${featuredArticle.id}`}
                  className="lg:row-span-2 relative group rounded-lg overflow-hidden cursor-pointer h-[360px] lg:h-auto"
                >
                  <img
                    src={featuredArticle.image}
                    alt={featuredArticle.title}
                    title={featuredArticle.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-500 text-background-50 rounded-full mb-3 whitespace-nowrap">
                      编辑精选
                    </span>
                    <h3 className="font-heading text-lg font-bold text-background-50 mb-2 line-clamp-2">
                      {featuredArticle.title}
                    </h3>
                    <span className="text-xs text-background-50/50">{featuredArticle.publishDate}</span>
                  </div>
                </Link>
              )}

              {remainingArticles.slice(0, 2).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="relative group rounded-lg overflow-hidden cursor-pointer h-[170px]"
                >
                  <img
                    src={article.image}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="font-heading text-sm font-bold text-background-50 mb-1 line-clamp-2">
                      {article.title}
                    </h3>
                    <span className="text-xs text-background-50/50">{article.publishDate}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* More articles list */}
        <section className="w-full bg-background-50 py-16 md:py-20">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center">
                全部<span className="text-accent-500"> 爆料</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {sortedArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex gap-4 p-4 bg-background-100 rounded-lg cursor-pointer hover:bg-background-200/70 transition-colors"
                >
                  <div className="w-32 h-24 flex-shrink-0 rounded-md overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-sm font-bold text-foreground-900 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{article.publishDate}</span>
                      <span><i className="ri-eye-line mr-1"></i>{article.views.toLocaleString()}</span>
                      <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                        {article.tags[0]}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}